import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "wrapper" }
const _hoisted_2 = { class: "header" }
const _hoisted_3 = {
  class: "help-icon",
  tabindex: "0"
}
const _hoisted_4 = { class: "help-tooltip" }
const _hoisted_5 = { class: "help-table" }
const _hoisted_6 = {
  key: 0,
  class: "loading apollo"
}
const _hoisted_7 = { class: "dashboard-container" }
const _hoisted_8 = ["src"]

import { computed, ref } from "vue";
import dayjs, { Dayjs } from "dayjs";
import { useQuery } from "@vue/apollo-composable";
import { GET_ADMIN_CFO_DASHBOARD_URL } from "@/graphql/user/queries";
import MonthPicker from "@/components/MonthPicker.vue";
import { useI18n } from "vue-i18n";
import enUK from "@/locales/home/en-UK.json";

type MessageSchema = typeof enUK;


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminCfoDashboard',
  setup(__props) {

const { t } = useI18n<[MessageSchema], "en-UK">({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": enUK,
  },
});

// MONTH
const defaultMonth = dayjs(new Date());
const selectedMonth = ref<Dayjs | null>(defaultMonth);
const selectedMonthName = computed(
  () => selectedMonth?.value?.format("MMMM YYYY") ?? "",
);

const setPrevMonth = () => {
  selectedMonth.value = selectedMonth.value?.subtract(1, "M") ?? defaultMonth;
};

const setNextMonth = () => {
  selectedMonth.value = selectedMonth.value?.add(1, "M") ?? defaultMonth;
};

const month = computed(() => selectedMonth.value?.format("YYYY-MM-DD") ?? null);

// Filter values (matching backend MetabaseService)
const monthAndYear = computed(
  () => selectedMonth.value?.format("YYYY-MM") ?? "",
);
const mMinus1 = computed(
  () => selectedMonth.value?.subtract(1, "month").format("YYYY-MM") ?? "",
);
const startMonthPivot = computed(
  () => selectedMonth.value?.startOf("month").format("YYYY-MM-DD") ?? "",
);
const endMonthPivot = computed(
  () =>
    selectedMonth.value?.add(5, "month").endOf("month").format("YYYY-MM-DD") ??
    "",
);
const dateRange = computed(
  () =>
    `${selectedMonth.value?.subtract(8, "month").format("YYYY-MM-DD") ?? ""}~${
      selectedMonth.value?.format("YYYY-MM-DD") ?? ""
    }`,
);
const twelveMonthsAgo = computed(
  () =>
    selectedMonth.value
      ?.subtract(12, "month")
      .startOf("month")
      .format("YYYY-MM-DD") ?? "",
);
const fiveYearsAgo = computed(
  () =>
    selectedMonth.value
      ?.subtract(5, "year")
      .startOf("month")
      .format("YYYY-MM-DD") ?? "",
);

const variables = computed(() => ({ month: month.value }));

const { result, loading } = useQuery<{ adminCfoDashboardUrl: string }>(
  GET_ADMIN_CFO_DASHBOARD_URL,
  variables,
);
const dashboardUrl = computed(() => result.value?.adminCfoDashboardUrl);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(MonthPicker, {
        label: selectedMonthName.value,
        onPrev: setPrevMonth,
        onNext: setNextMonth
      }, null, 8, ["label"]),
      _createElementVNode("div", _hoisted_3, [
        _cache[37] || (_cache[37] = _createElementVNode("span", { class: "help-icon-text" }, "?", -1)),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("table", _hoisted_5, [
            _cache[36] || (_cache[36] = _createElementVNode("thead", null, [
              _createElementVNode("tr", null, [
                _createElementVNode("th", null, "Query"),
                _createElementVNode("th", null, "Filter"),
                _createElementVNode("th", null, "Value")
              ])
            ], -1)),
            _createElementVNode("tbody", null, [
              _createElementVNode("tr", null, [
                _cache[0] || (_cache[0] = _createElementVNode("td", null, " Revenue flow pivot (start month (pivot) - end month (pivot)) ", -1)),
                _cache[1] || (_cache[1] = _createElementVNode("td", null, "start/end month (pivot)", -1)),
                _createElementVNode("td", null, _toDisplayString(startMonthPivot.value) + " → " + _toDisplayString(endMonthPivot.value), 1)
              ]),
              _createElementVNode("tr", null, [
                _cache[2] || (_cache[2] = _createElementVNode("td", null, " Total open invoices with comming due date (Month and year) ", -1)),
                _cache[3] || (_cache[3] = _createElementVNode("td", null, "month_and_year", -1)),
                _createElementVNode("td", null, _toDisplayString(monthAndYear.value), 1)
              ]),
              _createElementVNode("tr", null, [
                _cache[4] || (_cache[4] = _createElementVNode("td", null, " Total late invoice with no promised date (Month and year) ", -1)),
                _cache[5] || (_cache[5] = _createElementVNode("td", null, "month_and_year", -1)),
                _createElementVNode("td", null, _toDisplayString(monthAndYear.value), 1)
              ]),
              _createElementVNode("tr", null, [
                _cache[6] || (_cache[6] = _createElementVNode("td", null, "Total late invoices with promised date (Month and year)", -1)),
                _cache[7] || (_cache[7] = _createElementVNode("td", null, "month_and_year", -1)),
                _createElementVNode("td", null, _toDisplayString(monthAndYear.value), 1)
              ]),
              _createElementVNode("tr", null, [
                _cache[8] || (_cache[8] = _createElementVNode("td", null, "Total post-paid (m-1) to be paid (M-1)", -1)),
                _cache[9] || (_cache[9] = _createElementVNode("td", null, "m-1", -1)),
                _createElementVNode("td", null, _toDisplayString(mMinus1.value), 1)
              ]),
              _createElementVNode("tr", null, [
                _cache[10] || (_cache[10] = _createElementVNode("td", null, "Opened invoices with comming due date (Month and year)", -1)),
                _cache[11] || (_cache[11] = _createElementVNode("td", null, "month_and_year", -1)),
                _createElementVNode("td", null, _toDisplayString(monthAndYear.value), 1)
              ]),
              _createElementVNode("tr", null, [
                _cache[12] || (_cache[12] = _createElementVNode("td", null, "Late invoice with no promised date (Month and year)", -1)),
                _cache[13] || (_cache[13] = _createElementVNode("td", null, "month_and_year", -1)),
                _createElementVNode("td", null, _toDisplayString(monthAndYear.value), 1)
              ]),
              _createElementVNode("tr", null, [
                _cache[14] || (_cache[14] = _createElementVNode("td", null, " Current Post paid orders - expected paiments for next month (M-1) ", -1)),
                _cache[15] || (_cache[15] = _createElementVNode("td", null, "m-1", -1)),
                _createElementVNode("td", null, _toDisplayString(mMinus1.value), 1)
              ]),
              _createElementVNode("tr", null, [
                _cache[16] || (_cache[16] = _createElementVNode("td", null, " Pre-paid orders sent or with late approval (start month (pivot)) ", -1)),
                _cache[17] || (_cache[17] = _createElementVNode("td", null, "start_month_(pivot)", -1)),
                _createElementVNode("td", null, _toDisplayString(startMonthPivot.value), 1)
              ]),
              _cache[34] || (_cache[34] = _createElementVNode("tr", null, [
                _createElementVNode("td", null, " Validated pre-paid orders for next months (no dynamic filter) "),
                _createElementVNode("td", null, "—"),
                _createElementVNode("td", null, "—")
              ], -1)),
              _createElementVNode("tr", null, [
                _cache[18] || (_cache[18] = _createElementVNode("td", null, "Late invoices with promised date (Date range)", -1)),
                _cache[19] || (_cache[19] = _createElementVNode("td", null, "date_range", -1)),
                _createElementVNode("td", null, _toDisplayString(dateRange.value), 1)
              ]),
              _createElementVNode("tr", null, [
                _cache[20] || (_cache[20] = _createElementVNode("td", null, " Total uninvoiced without overlapping order (Month and year) ", -1)),
                _cache[21] || (_cache[21] = _createElementVNode("td", null, "month_and_year", -1)),
                _createElementVNode("td", null, _toDisplayString(monthAndYear.value), 1)
              ]),
              _createElementVNode("tr", null, [
                _cache[22] || (_cache[22] = _createElementVNode("td", null, " Total uninvoiced with overlap minus order deduction (Month and year) ", -1)),
                _cache[23] || (_cache[23] = _createElementVNode("td", null, "month_and_year", -1)),
                _createElementVNode("td", null, _toDisplayString(monthAndYear.value), 1)
              ]),
              _createElementVNode("tr", null, [
                _cache[24] || (_cache[24] = _createElementVNode("td", null, "To invoice (Month and year)", -1)),
                _cache[25] || (_cache[25] = _createElementVNode("td", null, "month_and_year", -1)),
                _createElementVNode("td", null, _toDisplayString(monthAndYear.value), 1)
              ]),
              _cache[35] || (_cache[35] = _createElementVNode("tr", null, [
                _createElementVNode("td", null, "Uninvoiced timesheets (no dynamic filter)"),
                _createElementVNode("td", null, "—"),
                _createElementVNode("td", null, "—")
              ], -1)),
              _createElementVNode("tr", null, [
                _cache[26] || (_cache[26] = _createElementVNode("td", null, " Total late invoice with past expected date (12 month in the past up to start month (pivot)) ", -1)),
                _cache[27] || (_cache[27] = _createElementVNode("td", null, "12m → start pivot", -1)),
                _createElementVNode("td", null, _toDisplayString(twelveMonthsAgo.value) + " → " + _toDisplayString(startMonthPivot.value), 1)
              ]),
              _createElementVNode("tr", null, [
                _cache[28] || (_cache[28] = _createElementVNode("td", null, " Total late invoices without past expected date (12 month in the past up to start month (pivot)) ", -1)),
                _cache[29] || (_cache[29] = _createElementVNode("td", null, "12m → start pivot", -1)),
                _createElementVNode("td", null, _toDisplayString(twelveMonthsAgo.value) + " → " + _toDisplayString(startMonthPivot.value), 1)
              ]),
              _createElementVNode("tr", null, [
                _cache[30] || (_cache[30] = _createElementVNode("td", null, " Late invoices with past expected date (5 years up in the past to start month (pivot)) ", -1)),
                _cache[31] || (_cache[31] = _createElementVNode("td", null, "5y → start pivot", -1)),
                _createElementVNode("td", null, _toDisplayString(fiveYearsAgo.value) + " → " + _toDisplayString(startMonthPivot.value), 1)
              ]),
              _createElementVNode("tr", null, [
                _cache[32] || (_cache[32] = _createElementVNode("td", null, " Late invoices without past expected date (5 years in the past up to start month (pivot)) ", -1)),
                _cache[33] || (_cache[33] = _createElementVNode("td", null, "5y → start pivot", -1)),
                _createElementVNode("td", null, _toDisplayString(fiveYearsAgo.value) + " → " + _toDisplayString(startMonthPivot.value), 1)
              ])
            ])
          ])
        ])
      ])
    ]),
    (_unref(loading))
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_unref(t)("loading")), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("iframe", {
        class: "metabase-dashboard",
        src: dashboardUrl.value
      }, null, 8, _hoisted_8)
    ])
  ]))
}
}

})